import { useEffect } from "react";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { Button, TwoStageButton } from "@jauntin/react-ui";
import { Selectors } from "@jauntin/reactables";
import RouteParser from "Helpers/RouteParser";
import { Alert, Card } from "react-bootstrap";
import AlertInvalidPolicy from "Features/Shared/Components/AlertInvalidPolicy";
import AlertSelectionRequired from "Features/Shared/Components/AlertSelectionRequired";
import YesNoField from "Features/Shared/Components/YesNoField";
import PopoverTrigger from "Features/Shared/Components/Popover";
import Breadcrumbs from "../BreadCrumbs/Breadcrumbs";
import { RxCoverageApplicationProp } from "../../Rx/RxCoverageApplication/RxCoverageApplication";

import * as EventTypesSelectors from "../../Rx/RxCoverageApplication/Selectors/eventTypes.selector";
import * as FormSelectors from "../../Rx/RxCoverageApplication/Selectors/form.selector";

const UnderwritingQuestions = ({
  rxCoverageApplication: [state, actions],
}: {
  rxCoverageApplication: RxCoverageApplicationProp;
}) => {
  const location = useLocation();
  const history = useHistory();

  const selectedEvent = EventTypesSelectors.getSelectedEvent(state);

  // If browser loads on this page we will select event by eventType path parameter
  const { eventType: eventTypeId } = useParams() as { eventType: string };

  // Users are able to land directly on a URL with the event selected and not have to select
  // it on the previous screen, as such find and select it if required.
  useEffect(() => {
    if (!selectedEvent) {
      const eventType = EventTypesSelectors.getEventType(state, eventTypeId);
      eventType && actions.applicationForm.selectEventTypeIfNeeded(eventType);
    }
  }, [selectedEvent, eventTypeId, state, actions]);

  if (!selectedEvent) return;

  const {
    underwritingQuestions: { promoter, security, lossClaims, vendor },
  } = FormSelectors.getRootValue(state);

  const underwritingQuestionsControl = FormSelectors.getControl(
    state,
    "underwritingQuestions",
  );

  const hasVisibleError = Selectors.hasControlTouchError(
    state.applicationForm,
    "underwritingQuestions",
  );
  const buttonTouched = state.touchButtons.underwritingQuestions;
  const underWritingQuestionsValid = underwritingQuestionsControl.valid;
  const actionEnabled = buttonTouched || underWritingQuestionsValid;
  const disabled =
    hasVisibleError || (actionEnabled && !underWritingQuestionsValid);

  const showSelectionRequiredPromoter =
    buttonTouched &&
    FormSelectors.getControl(state, "underwritingQuestions.promoter")?.errors
      .required;

  const showSelectionRequiredSecurity =
    buttonTouched &&
    FormSelectors.getControl(state, "underwritingQuestions.security")?.errors
      .required;
  const showSelectionRequiredLossClaims =
    buttonTouched &&
    FormSelectors.getControl(state, "underwritingQuestions.lossClaims")?.errors
      .required;
  const showSelectionRequiredVendor =
    buttonTouched &&
    FormSelectors.getControl(state, "underwritingQuestions.vendor")?.errors
      .required;
  const showVendorMessage =
    FormSelectors.getControl(state, "underwritingQuestions.vendor")?.value ===
    "no";

  return (
    <>
      <Breadcrumbs />
      <div className="eventSelect d-flex justify-content-center">
        <div className="eventSelect card--width-lg card--main">
          <Card.Header className="border-0">
            <div className="text-center w-100">
              <h1>
                Get your{" "}
                <span className="text-lowercase">
                  {selectedEvent.headerName}
                </span>{" "}
                insurance quote.
              </h1>
              {selectedEvent.subtitle && (
                <div className="h7 pt-2">{selectedEvent.subtitle}</div>
              )}
              <p>{selectedEvent.eventCoverageNote}</p>
            </div>
          </Card.Header>
          <Card.Body>
            <>
              <AlertInvalidPolicy
                show={underwritingQuestionsControl.errors.eligibility}
              />
              {lossClaims !== undefined && (
                <>
                  <YesNoField
                    name="underwritingQuestions.lossClaims"
                    className="my-0 mb-0"
                  >
                    If you have held this event before, have there been any
                    losses or claims?
                  </YesNoField>
                  <AlertSelectionRequired
                    show={showSelectionRequiredLossClaims}
                  />
                </>
              )}
              {security !== undefined && (
                <>
                  <YesNoField
                    name="underwritingQuestions.security"
                    className="mt-3 mb-0"
                  >
                    Will there be any armed security who are not police
                    officers?
                  </YesNoField>
                  <AlertSelectionRequired
                    show={showSelectionRequiredSecurity}
                  />
                </>
              )}
              {promoter !== undefined && (
                <>
                  <YesNoField
                    name="underwritingQuestions.promoter"
                    className="mt-3 mb-0"
                  >
                    <>
                      <span className="mr-1 align-middle">
                        Are you a promoter?
                      </span>
                      <PopoverTrigger
                        icon={
                          <i className="fal fa-question-circle c-teal pt-2"></i>
                        }
                        content="A promoter is a company in the business of financing or organizing events for profit. One who assumes the financial responsibilities of the event."
                      />
                    </>
                  </YesNoField>
                  <AlertSelectionRequired
                    show={showSelectionRequiredPromoter}
                  />
                </>
              )}
              {vendor !== undefined && (
                <>
                  <YesNoField
                    name="underwritingQuestions.vendor"
                    className="mt-3 mb-0"
                  >
                    <>
                      <span className="mr-1 align-middle">
                        Are you a vendor?
                      </span>
                      <PopoverTrigger
                        icon={
                          <i className="fal fa-question-circle c-teal pt-2"></i>
                        }
                        content="Yes = I am a vendor who IS NOT renting space from the venue or event organizer. No = I am a vendor who IS renting space from the venue or event organizer."
                      />
                    </>
                  </YesNoField>
                  <AlertSelectionRequired show={showSelectionRequiredVendor} />
                  <Alert
                    show={showVendorMessage}
                    variant="primary"
                    className="mw--635 mx-auto"
                  >
                    <i className="fal fa-info-circle alert__icon" />
                    Do not count yourself as a vendor in the vendor section of
                    this application.
                  </Alert>
                </>
              )}
              <div className="row justify-content-center mt-5">
                <div className="col-auto">
                  <Link to={new RouteParser().linkToEventTypePage()}>
                    <Button
                      onClick={() => undefined}
                      text="Back"
                      className="btn-link"
                    />
                  </Link>
                </div>
                <div className="col-auto">
                  <TwoStageButton
                    disabled={disabled}
                    actionEnabled={actionEnabled}
                    action={() => {
                      history.push(
                        RouteParser.create(
                          location.pathname,
                        ).linkToEligibilityPage(),
                      );
                    }}
                    touch={() => actions.touchButton("underwritingQuestions")}
                    text="Continue"
                    className="btn btn-primary"
                  />
                </div>
              </div>
            </>
          </Card.Body>
        </div>
      </div>
    </>
  );
};

export default UnderwritingQuestions;
